import { ChevronRight, TriangleAlert } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { measureValueOf } from './breakdown-columns'
import type {
  ReportDimension,
  ReportMeasure,
  ReportRow,
  ReportTotals,
} from '@/features/reports/api/use-sales-report-query'
import { ShareBar } from '@/features/reports/components/share-bar'
import { DeltaBadge } from '@/features/reports/components/delta-badge'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import {
  formatRowLabel,
  hourRowsSpanDays,
} from '@/features/reports/lib/dimension-labels'
import { cn } from '@/lib/utils'

interface BreakdownCardsProps {
  dimension: ReportDimension
  measure: ReportMeasure
  rows: Array<ReportRow>
  total: ReportTotals
  previous: ReportTotals | undefined
  /** Null when this dimension has no level below it — cards are then not tappable. */
  onDrill: ((row: ReportRow) => void) | null
  loading?: boolean
}

/**
 * The same breakdown as a stack of cards, for phones (CHG-2026-09-manager-reporting).
 *
 * A seven-column table on a 375px screen is a table nobody reads: it either scrolls sideways —
 * hiding the very columns that carry the answer — or shrinks the figures below legibility. So the
 * row becomes a card, and the columns become a hierarchy: the label and the measure on the first
 * line because that is the question, the share as the bar underneath, and the supporting counts in
 * one muted line below that.
 *
 * The total leads as a filled card rather than a pinned header row: on a phone there is no frozen
 * header to pin it to, and the reader's first question is the total either way.
 */
export function BreakdownCards({
  dimension,
  measure,
  rows,
  total,
  previous,
  onDrill,
  loading,
}: BreakdownCardsProps) {
  const { t } = useTranslation('reports')

  const totalValue = measure === 'units' ? total.units : total.revenueCents
  const withDate = hourRowsSpanDays(rows)
  const format = (n: number) =>
    measure === 'units' ? formatNumber(n) : formatCurrency(n)

  if (loading) return <Skeleton className="h-96 w-full" />

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border py-16 text-center text-sm text-muted-foreground">
        {t('empty')}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="rounded-xl bg-foreground px-4 py-3.5 text-background">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs text-background/70">
            {t('columns.total')}
          </span>
          <DeltaBadge
            current={totalValue}
            previous={
              previous
                ? measure === 'units'
                  ? previous.units
                  : previous.revenueCents
                : null
            }
            tone="inverse"
          />
        </div>
        <div className="mt-0.5 text-2xl font-bold tabular-nums">
          {format(totalValue)}
        </div>
        <div className="mt-1 text-xs tabular-nums text-background/70">
          {supportLine(t, total, measure)}
        </div>
      </div>

      {rows.map((row) => {
        const value = measureValueOf(row, measure)
        const clickable = onDrill != null
        return (
          <div
            key={row.key}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onDrill(row) : undefined}
            onKeyDown={
              clickable
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onDrill(row)
                    }
                  }
                : undefined
            }
            className={cn(
              'rounded-xl border border-border bg-card px-4 py-3',
              clickable && 'cursor-pointer active:bg-muted/60',
              row.isCollector && 'border-warning/40 bg-warning/5',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                {row.isCollector ? (
                  <TriangleAlert
                    className="h-3.5 w-3.5 shrink-0 text-warning"
                    aria-hidden="true"
                  />
                ) : null}
                <span className="truncate">
                  {formatRowLabel(t, dimension, row, { withDate })}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                {format(value)}
              </span>
            </div>

            {row.parentLabel ? (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {row.parentLabel}
              </div>
            ) : null}

            <ShareBar
              share={totalValue > 0 ? value / totalValue : 0}
              tone={row.isCollector ? 'warning' : 'magnitude'}
              className="mt-2"
            />

            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="truncate text-xs tabular-nums text-muted-foreground">
                {supportLine(t, row, measure)}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <DeltaBadge
                  current={value}
                  previous={
                    measure === 'units'
                      ? row.previousUnits
                      : row.previousRevenueCents
                  }
                />
                {clickable ? (
                  <ChevronRight
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                ) : null}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * `254 Bons · Ø € 8,43 · 483 Stk.` — the columns the table shows separately, on one line.
 *
 * The measure itself is left out: it is already the big number above, and repeating it here would
 * cost the width the counts need.
 */
function supportLine(
  t: (key: string, options?: Record<string, unknown>) => string,
  row: { revenueCents: number; units: number; bons: number },
  measure: ReportMeasure,
): string {
  const parts = [t('cards.bons', { value: formatNumber(row.bons) })]
  if (row.bons > 0) {
    parts.push(
      t('cards.averageBon', {
        value: formatCurrency(Math.round(row.revenueCents / row.bons)),
      }),
    )
  }
  parts.push(
    measure === 'units'
      ? t('cards.revenue', { value: formatCurrency(row.revenueCents) })
      : t('cards.units', { value: formatNumber(row.units) }),
  )
  return parts.join(' · ')
}
